import '../../App.scss';
import { sanitizeHtml } from '../../util/sanitizeHtml';
import { Route, Routes, useNavigate, useLocation } from "react-router-dom"
import { getTranslatedText } from "../../util/util"
import usePageTranslation from "../../hooks/ui/usePageTranslation";
import { Suspense, useEffect, useRef } from "react";
import React from "react";
import KioskHandler from "./KioskHandler";
import { useGetCompanyThemeDetailsMutation } from "../../api-integration/secure/api.kiosk";
import {useLazyGetSessionStatusQuery} from "../../api-integration/secure/api.account";
import { normalizeAPIResponse } from "../../util/normalizeAPIResponse";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "../../theme/ThemeContext";
import { updateAlertMessage } from "../../api-integration/commonSlice";
import KioskNonAuthGuard from "../../guards/kiosk-non-auth.guard";
import KioskAuthGuard from "../../guards/kiosk-auth.guard";
import KioskExternalGateway from "../pages/public/kiosk-external-gateway";
import { useExtendSessionMutation } from "../../api-integration/public/common";
import {setAuthChecking, setKioskSession, clearKioskSession} from "../../api-integration/kioskAuthSlice";
import Alert from '../../components/common/alert/alert';
import { classifyError } from '../../util/errorClassifier';
import InitializationErrorScreen from '../../components/common/error/InitializationErrorScreen';
import { useState } from 'react';

const KioskLogin = React.lazy(() => import("../pages/public/kiosk-login"));
const KioskConsent = React.lazy(() => import("../pages/public/kiosk-consent"));
const KioskCodeVerify = React.lazy(() => import("../pages/public/kiosk-code-verify"));
const KioskInvalidInvite = React.lazy(() => import("../pages/public/kiosk-invalid-invite"));
const KioskHome = React.lazy(() => import("../pages/secure/kiosk-home"));
const KioskLogout = React.lazy(() => import("../pages/secure/kiosk-logout"));
// const KioskLoginDev = React.lazy(() => import("../pages/public/kiosk-login-dev"));


const KioskRoutes = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const {setTheme} = useTheme();
    const PageTranslation = usePageTranslation();

    const { isAuthChecking } = useSelector((state: any) => state.kioskAuthSlice);
    const { isSessionExpired, reloadPageAfterSessionExpired, isRefreshTokenExpired } = useSelector((state: any) => state.commonSlice);
    const fullPageLoaderSelector = useSelector((state: any) => state.commonSlice.fullPageLoader);

    const [getCompanyThemeDetailsAPI, getCompanyThemeDetailsResult] = useGetCompanyThemeDetailsMutation();
    const [getSessionStatusAPI, getSessionStatusResult] = useLazyGetSessionStatusQuery();

    const [extendSesionAPI, { data: extendSessionResponse, isSuccess: isSessionExtendedSuccess, isError: isErrorWhileExtendingSession }] 
    = useExtendSessionMutation();

    const sessionExtendAttemptedRef = useRef(false);
    const [initErrorClassification, setInitErrorClassification] = useState<'NETWORK_ERROR' | 'SERVER_ERROR' | 'UNKNOWN_ERROR' | null>(null);

    useEffect(() => {
        if (isSessionExtendedSuccess) {
          if (extendSessionResponse?.success === true) {
            if (reloadPageAfterSessionExpired) {
              window.location.reload();
            }
          } else {
            dispatch(updateAlertMessage({ status: 'success', message: "Session could not be extended" }));
            dispatch(clearKioskSession());
          }
        }
        else if (isErrorWhileExtendingSession) {
            dispatch(updateAlertMessage({ status: 'success', message: "Session could not be extended" }));
            dispatch(clearKioskSession());
        }
    }, [extendSessionResponse, isSessionExtendedSuccess, isErrorWhileExtendingSession]);

    useEffect(() => {
        if (isRefreshTokenExpired) {
            dispatch(clearKioskSession());
        }
    }, [isRefreshTokenExpired])

    useEffect(() => {
        if (isSessionExpired === true && !sessionExtendAttemptedRef.current) {
          sessionExtendAttemptedRef.current = true;
          extendSesionAPI({});
        }
      }, [isSessionExpired]);

    useEffect(() => {
        if (location.pathname.toLowerCase().endsWith('/exit')) {
            dispatch(clearKioskSession());
        }
    }, [location.pathname]);

    useEffect(() => {
        getCompanyThemeDetailsAPI({});
    },[]);

    useEffect(() => {
        if(getCompanyThemeDetailsResult?.isSuccess || getCompanyThemeDetailsResult?.isError) {
            const response = normalizeAPIResponse(getCompanyThemeDetailsResult);
            if(response?.success === true) {
                const companyThemeData = response?.data?.companyThemeDetails || {};
                const themeDetails = companyThemeData?.themeDetails || {};
                const companyDetails = companyThemeData?.companyDetails || {};
                localStorage.setItem("kioskCompanyDetails", JSON.stringify(companyDetails));
                setTheme(themeDetails);
            }
            else {
                dispatch(updateAlertMessage({status: 'error', message: response.message}));
                navigate('/login');
            }
        }
    },[getCompanyThemeDetailsResult?.isSuccess, getCompanyThemeDetailsResult?.isError]);

    useEffect(() => {
        const isExternalRoute = location.pathname.toLowerCase().startsWith('/external');

        if(!isExternalRoute) {
            dispatch(setAuthChecking(true));
            getSessionStatusAPI({});
        }
        else {
            dispatch(setAuthChecking(false));
        }
    }, []);

    useEffect(() => {
        if(getSessionStatusResult.isSuccess || getSessionStatusResult.isError) {
            const response = normalizeAPIResponse(getSessionStatusResult);
            if(response?.success === true && response?.data?.user) {
                const userData = response.data.user;
                dispatch(
                    setKioskSession({
                        user: {
                            userEmail: userData.userEmail,
                            userBusinessEmail: userData.userBusinessEmail,
                            userId: userData.userId,
                            firstName: userData.firstName,
                            lastName: userData.lastName,
                            picPath: userData.picPath,
                        }
                    })
                );
                setInitErrorClassification(null);
            }
            else {
                if (getSessionStatusResult.isError) {
                    const classification = classifyError(getSessionStatusResult.error);
                    if (classification === 'NETWORK_ERROR' || classification === 'SERVER_ERROR' || classification === 'UNKNOWN_ERROR') {
                        setInitErrorClassification(classification);
                        dispatch(setAuthChecking(false));
                        return;
                    }
                }
                dispatch(clearKioskSession());
            }
            dispatch(setAuthChecking(false));
        }
    },[getSessionStatusResult?.isSuccess, getSessionStatusResult?.isError]);

    if (initErrorClassification) {
        return (
            <InitializationErrorScreen 
                classification={initErrorClassification} 
            />
        );
    }

    if (isAuthChecking) {
        return (
            <div className='fullpage-loader'>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <Suspense fallback={<div dangerouslySetInnerHTML={{ __html: sanitizeHtml(getTranslatedText('loading.message', PageTranslation)) }}></div>}>
            <Routes>
                <Route path="/" element={<KioskNonAuthGuard><KioskLogin /></KioskNonAuthGuard>} />
                <Route path="/invite" element={<KioskHandler />} />
                <Route path="/login" element={<KioskNonAuthGuard><KioskLogin /></KioskNonAuthGuard>} />
                <Route path="/consent" element={<KioskConsent />} />
                <Route path="/verify" element={<KioskAuthGuard><KioskCodeVerify /></KioskAuthGuard>} />
                <Route path="/invalid-invite" element={<KioskInvalidInvite />} />
                <Route path="/external/:id" element={<KioskNonAuthGuard><KioskExternalGateway /></KioskNonAuthGuard>} ></Route>
                <Route path="/home" element={<KioskAuthGuard><KioskHome /></KioskAuthGuard>} />
                <Route path="/logout" element={<KioskLogout />} />
                <Route path="*" element={<KioskInvalidInvite />} />
                {/* <Route path="/login-dev" element={<KioskNonAuthGuard><KioskLoginDev /></KioskNonAuthGuard>} /> */}
            </Routes>
            </Suspense>
            {fullPageLoaderSelector && 
            <div className='fullpage-loader' >
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                    Loading…
                    </span>
                </div>
                </div>
            }
            <Alert />
        </>
    )
}
export default KioskRoutes;